// components/layout/Header.js
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";

export default function Header() {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const router = useRouter();
	const menuRef = useRef(null);
	const buttonRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				isMenuOpen &&
				menuRef.current &&
				!menuRef.current.contains(event.target) &&
				buttonRef.current &&
				!buttonRef.current.contains(event.target)
			) {
				setIsMenuOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isMenuOpen]);

	const linkClass = (path) => {
		const baseClasses = "hover:bg-purple-700 whitespace-nowrap";
		const responsiveClasses = "py-2 px-5 block";
		if (router.pathname === path) {
			return `${baseClasses} ${responsiveClasses} text-white font-bold`;
		}
		return `${baseClasses} ${responsiveClasses} text-white font-medium`;
	};

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	return (
		<nav id="main-header" className="bg-purple fixed w-full z-10 transition-all duration-300 ease-in-out">
			<div className="container mx-auto px-4 py-6">
				{/* Mobile Layout */}
				<div className="lg:hidden flex justify-between items-center h-12 px-1 sm:h-16 sm:px-4">
					{/* Logo - Left */}
					<div>
						<Link href="/" onClick={() => setIsMenuOpen(false)} className="flex items-center">
							<div className="text-white font-semibold text-base sm:text-lg leading-tight">
								<div>RIVERVIEW</div>
								<div>RESIDENCES</div>
							</div>
						</Link>
					</div>

					{/* Book a Tour Button - Center */}
					<div>
						<Link
							href="https://summit-properties.rentiq.com/riverview-residences/tours"
							target="_blank"
							rel="noopener noreferrer"
							className="font-bold py-2.5 px-4 sm:py-3 sm:px-6 rounded bg-white text-purple text-sm sm:text-base text-center transition-all duration-300 transform hover:scale-105 hover:shadow-lg whitespace-nowrap min-h-[44px] flex items-center"
							onClick={() => setIsMenuOpen(false)}
						>
							BOOK A TOUR
						</Link>
					</div>

					{/* Hamburger Menu - Right */}
					<div>
						<button
							ref={buttonRef}
							onClick={toggleMenu}
							className="text-white focus:outline-none p-2 min-h-[44px] min-w-[44px] sm:min-h-[48px] sm:min-w-[48px] flex items-center justify-center"
							aria-label="Toggle menu"
						>
							<div className="relative w-6 h-6 sm:w-7 sm:h-7">
								<span
									className={`block absolute h-0.5 w-full bg-white transform transition duration-300 ease-in-out ${
										isMenuOpen ? "rotate-45 top-2.5" : "rotate-0 top-1"
									}`}
								></span>
								<span
									className={`block absolute h-0.5 w-full bg-white transform transition duration-300 ease-in-out ${
										isMenuOpen ? "opacity-0" : "opacity-100 top-2.5"
									}`}
								></span>
								<span
									className={`block absolute h-0.5 w-full bg-white transform transition duration-300 ease-in-out ${
										isMenuOpen ? "-rotate-45 top-2.5" : "rotate-0 top-4"
									}`}
								></span>
							</div>
						</button>
					</div>
				</div>

				{/* Desktop Layout - Improved Responsiveness */}
				<div className="hidden lg:flex justify-between items-center">
					<Link
						href="/"
						className="text-lg lg:text-lg xl:text-xl text-white font-medium"
						onClick={() => setIsMenuOpen(false)}
					>
						RIVERVIEW RESIDENCES
					</Link>
					<div className="flex items-center lg:space-x-1 xl:space-x-4">
						<Link href="/" className={linkClass("/")}>
							HOME
						</Link>
						<Link href="/suites" className={linkClass("/suites")}>
							SUITES
						</Link>
						<Link href="/amenities" className={linkClass("/amenities")}>
							AMENITIES
						</Link>
						<Link href="/gallery" className={linkClass("/gallery")}>
							GALLERY
						</Link>
						<Link href="/location" className={linkClass("/location")}>
							LOCATION
						</Link>
						<Link href="/contact" className={linkClass("/contact")}>
							CONTACT
						</Link>
						<Link
							href="https://summit-properties.rentiq.com/riverview-residences/tours"
							target="_blank"
							rel="noopener noreferrer"
							className="lg:ml-2 xl:ml-6 font-bold py-2 lg:px-3 xl:px-6 rounded bg-white text-purple transition-all duration-300 transform hover:scale-105 hover:shadow-lg whitespace-nowrap text-sm xl:text-base"
						>
							BOOK A TOUR
						</Link>
					</div>
				</div>

				{/* Mobile Menu Dropdown */}
				<div
					ref={menuRef}
					className={`lg:hidden ${isMenuOpen ? "block" : "hidden"} absolute top-full left-0 w-full bg-purple shadow-md`}
				>
					<div className="mb-4">
						<Link href="/" className={linkClass("/")} onClick={() => setIsMenuOpen(false)}>
							HOME
						</Link>
						<Link href="/suites" className={linkClass("/suites")} onClick={() => setIsMenuOpen(false)}>
							SUITES
						</Link>
						<Link href="/amenities" className={linkClass("/amenities")} onClick={() => setIsMenuOpen(false)}>
							AMENITIES
						</Link>
						<Link href="/gallery" className={linkClass("/gallery")} onClick={() => setIsMenuOpen(false)}>
							GALLERY
						</Link>
						<Link href="/location" className={linkClass("/location")} onClick={() => setIsMenuOpen(false)}>
							LOCATION
						</Link>
						<Link href="/contact" className={linkClass("/contact")} onClick={() => setIsMenuOpen(false)}>
							CONTACT
						</Link>
					</div>
				</div>
			</div>
		</nav>
	);
}
